import { AZURE_MARKETPLACE_URL, SECTION_IDS } from '../lib/links';
import maskProcurement from '../assets/v2-mkt-procurement-mask.png';
import maskNative from '../assets/v2-mkt-native-mask.png';
import maskMacc from '../assets/v2-mkt-macc-mask.png';
import arrowDown from '../assets/v2-arrow-down.svg';

/**
 * "Available on Microsoft Azure Marketplace" — Figma node 2:459.
 *
 * Design values (at the 1920 reference width):
 *  · band     white, px-133 py-80, flex-col items-center, gap-36
 *  · heading  Inter 700 48/68, centred, black, w-875
 *  · copy     Inter 400 18/24, centred, black, "ORBIS" bold #E87722
 *  · row      gap-56, justify-center
 *  · card     w-490.667, p-28, r-12, 1px #EDF0F2, white, gap-24
 *  · tile     80×80, r-6, fill rgba(232,119,34,0.08); 48×48 mask tinted #E87722
 *             at (16, 16) inside it
 *  · h3       Inter 600 24/30.018, black
 *  · body     Inter 400 16/24, #1A1A1A
 *  · CTAs     gap-32; filled #0078D4 Inter 700 18px; underlined #0078D4
 *             Inter 500 18px + 24px arrow, gap-14
 *
 * The icons are alpha masks tinted with the brand orange, not inline SVG —
 * #E87722 is exactly primary-500, so the token is used rather than a raw hex.
 * Each card's "Read Article →" is `hidden` in the file (nodes 2:475, 2:485,
 * 2:494), so it is not rendered.
 */

interface Benefit {
  title: string;
  copy: string;
  mask: string;
}

const BENEFITS: readonly Benefit[] = [
  {
    title: 'Enterprise Procurement',
    copy: 'Purchase through your existing Azure subscription and procurement process.',
    mask: maskProcurement,
  },
  {
    title: 'Azure-Native',
    copy: 'Built and hosted entirely on Microsoft Azure infrastructure.',
    mask: maskNative,
  },
  {
    title: 'MACC Eligible',
    copy: 'Marketplace purchases count against your Microsoft Azure Committed Spend.',
    mask: maskMacc,
  },
];

export function AzureMarketplace() {
  return (
    <section
      id={SECTION_IDS.marketplace}
      className="w-full scroll-mt-16 bg-white px-5 py-12 sm:px-6 md:px-8 lg:scroll-mt-24 lg:px-10 lg:py-20 xl:px-16 2xl:px-[133px]"
    >
      <div className="mx-auto flex max-w-[1600px] flex-col items-center gap-8 lg:gap-9">
        <div className="flex w-full max-w-[875px] flex-col gap-5 text-center lg:gap-[26px]">
          <h2 className="m-0 text-[28px] font-bold leading-[1.2] text-black sm:text-[34px] lg:text-[40px] lg:leading-[58px] 2xl:text-5xl 2xl:leading-[68px]">
            Available on Microsoft Azure Marketplace
          </h2>
          <p className="m-0 text-base leading-6 text-black lg:text-lg">
            Deploy WisdomX <strong className="font-bold text-primary-500">ORBIS</strong> through
            Azure Marketplace with enterprise procurement, Azure-native deployment and Microsoft
            commercial benefits where applicable.
          </p>
        </div>

        <ul className="grid w-full list-none grid-cols-1 justify-center gap-8 p-0 md:grid-cols-2 lg:grid-cols-3 lg:gap-14">
          {BENEFITS.map((b) => (
            <li
              key={b.title}
              className="flex flex-col gap-6 rounded-xl border border-solid border-landing-card-line bg-white p-7 text-left"
            >
              <span className="flex size-20 shrink-0 items-center justify-center rounded-md bg-landing-icon-tint">
                <span
                  aria-hidden="true"
                  className="block size-12 bg-primary-500"
                  style={{
                    maskImage: `url(${b.mask})`,
                    WebkitMaskImage: `url(${b.mask})`,
                    maskRepeat: 'no-repeat',
                    WebkitMaskRepeat: 'no-repeat',
                    maskSize: 'contain',
                    WebkitMaskSize: 'contain',
                    maskPosition: 'center',
                    WebkitMaskPosition: 'center',
                  }}
                />
              </span>
              <div className="flex flex-col gap-5 lg:gap-[22px]">
                <h3 className="m-0 text-xl font-semibold leading-[30px] text-black lg:text-2xl">
                  {b.title}
                </h3>
                <p className="m-0 text-base leading-6 text-landing-card-copy">{b.copy}</p>
              </div>
            </li>
          ))}
        </ul>

        <div className="flex w-full flex-col items-stretch gap-5 sm:w-auto sm:flex-row sm:items-center sm:gap-8">
          <a
            href={AZURE_MARKETPLACE_URL}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center rounded-lg bg-landing-azure px-[30px] py-[17px] text-lg font-bold text-white no-underline transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-landing-azure"
          >
            View on Azure Marketplace
          </a>
          <a
            href={AZURE_MARKETPLACE_URL}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-center gap-3.5 text-lg font-medium text-landing-azure underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-landing-azure"
          >
            Learn more about Azure Marketplace
            <img
              src={arrowDown}
              alt=""
              aria-hidden="true"
              width={24}
              height={24}
              className="size-6 -rotate-90"
            />
          </a>
        </div>
      </div>
    </section>
  );
}
